import React, { useEffect, useRef, useState } from 'react';
import { removeSignatureBackground } from '../utils/signatureBackground';

// Shared pieces of the TAEPP screens. Styles live under `.taepp` in index.css, where
// every colour pair is checked for >= 6:1 contrast in light and dark mode. Anything
// using these must sit inside an element with className="taepp".

// Modal title: icon tile + title + one-line subtitle (pass inside <Modal title={...}>).
export function TaeppTitle({ icon, title, subtitle }) {
  return (
    <span className="taepp tp-title">
      <span className="tp-ic" aria-hidden="true"><i className={`bi ${icon}`} /></span>
      <span>
        <span className="tp-ttl">{title}</span>
        {subtitle && <span className="tp-sub">{subtitle}</span>}
      </span>
    </span>
  );
}

export const TAEPP_STATUS = {
  pending:   { label: 'Pending',   cls: 'warn', icon: 'bi-hourglass-split' },
  approved:  { label: 'Approved',  cls: 'ok',   icon: 'bi-check-circle' },
  rejected:  { label: 'Rejected',  cls: 'bad',  icon: 'bi-x-circle' },
  cancelled: { label: 'Cancelled', cls: 'mute', icon: 'bi-slash-circle' },
};

export function TaeppPill({ status }) {
  const cfg = TAEPP_STATUS[status] || { label: status, cls: 'mute', icon: 'bi-dot' };
  return <span className={`tp-pill ${cfg.cls}`}><i className={`bi ${cfg.icon}`} aria-hidden="true" />{cfg.label}</span>;
}

export function TaeppNote({ icon = 'bi-info-circle', children }) {
  return <div className="tp-note"><i className={`bi ${icon}`} aria-hidden="true" /><span>{children}</span></div>;
}

const PAD_W = 800, PAD_H = 280;   // drawing resolution; the pad is shown scaled to fit
const UPLOAD_MAX_W = 900;
const UPLOAD_MAX_BYTES = 5 * 1024 * 1024;

// A signature is saved, drawn, or uploaded; each ends up as a PNG data URL passed to
// onChange ('' = use the saved one, null = nothing usable yet). Uploaded pictures are
// scaled down first, and their background is removed (optional, on by default) so only
// the ink is stamped on the PDF.
export function SignatureInput({ hasSaved, savedImage, onChange }) {
  const [mode, setMode] = useState(hasSaved ? 'saved' : 'draw');
  const [upload, setUpload] = useState(null);          // { canvas } source pixels
  const [removeBg, setRemoveBg] = useState(true);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [over, setOver] = useState(false);
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const drawing = useRef(false);
  const inked = useRef(false);

  // Switching method starts that method from scratch.
  useEffect(() => {
    inked.current = false;
    setUpload(null);
    setPreview(null);
    setError('');
    onChange(mode === 'saved' ? '' : null);
    const c = canvasRef.current;
    if (c) c.getContext('2d').clearRect(0, 0, PAD_W, PAD_H);
  }, [mode]);

  // Rebuild the processed upload whenever the picture or the checkbox changes.
  useEffect(() => {
    if (!upload) return;
    const result = removeBg ? removeSignatureBackground(upload.canvas) : upload.canvas;
    const dataUrl = result.toDataURL('image/png');
    setPreview(dataUrl);
    onChange(dataUrl);
  }, [upload, removeBg]);

  // ── draw ──
  const point = e => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (PAD_W / r.width), y: (e.clientY - r.top) * (PAD_H / r.height) };
  };
  const down = e => {
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = point(e);
    ctx.lineWidth = 4; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#0b1f5c';
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + 0.1, y + 0.1); ctx.stroke();
  };
  const move = e => {
    if (!drawing.current) return;
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = point(e);
    ctx.lineTo(x, y); ctx.stroke();
    inked.current = true;
  };
  const up = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (inked.current) onChange(canvasRef.current.toDataURL('image/png'));
  };
  const clearPad = () => {
    canvasRef.current.getContext('2d').clearRect(0, 0, PAD_W, PAD_H);
    inked.current = false;
    onChange(null);
  };

  // ── upload (pick or drop) ──
  const loadFile = file => {
    setError('');
    if (!file) return;
    if (!/^image\/(png|jpeg)$/.test(file.type)) { setError('Choose a PNG or JPG image.'); return; }
    if (file.size > UPLOAD_MAX_BYTES) { setError('That image is larger than 5 MB.'); return; }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, UPLOAD_MAX_W / img.width);
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(img.width * scale));
      c.height = Math.max(1, Math.round(img.height * scale));
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      setUpload({ canvas: c });
    };
    img.onerror = () => { URL.revokeObjectURL(url); setError('That image could not be read.'); };
    img.src = url;
  };
  const onDrop = e => {
    e.preventDefault();
    setOver(false);
    loadFile(e.dataTransfer.files?.[0]);
  };
  const resetUpload = () => { setUpload(null); setPreview(null); setError(''); onChange(null); };

  const modes = [
    ...(hasSaved ? [['saved', 'Saved', 'bi-bookmark-check']] : []),
    ['draw', 'Draw', 'bi-pencil'],
    ['upload', 'Upload', 'bi-upload'],
  ];

  return (
    <div>
      <div className="tp-chips" role="radiogroup" aria-label="Signature method" style={{ marginBottom: 10 }}>
        {modes.map(([key, label, icon]) => (
          <button key={key} type="button" role="radio" aria-checked={mode === key}
            className={`tp-chip${mode === key ? ' on' : ''}`} onClick={() => setMode(key)}>
            <i className={`bi ${icon}`} aria-hidden="true" />{label}
          </button>
        ))}
      </div>

      {mode === 'saved' && (
        <>
          <div className="tp-checker"><img src={savedImage} alt="Your saved signature" /></div>
          <div className="tp-hint" style={{ marginTop: 8 }}>Your saved signature. Choose Draw or Upload to replace it.</div>
        </>
      )}

      {mode === 'draw' && (
        <>
          <div className="tp-pad">
            <canvas ref={canvasRef} width={PAD_W} height={PAD_H} aria-label="Signature pad"
              style={{ width: '100%', height: 'auto', aspectRatio: `${PAD_W} / ${PAD_H}`, display: 'block', cursor: 'crosshair' }}
              onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} onPointerCancel={up} />
          </div>
          <div className="tp-row" style={{ marginTop: 8 }}>
            <span className="tp-hint">Sign inside the box with your mouse, finger or stylus.</span>
            <button type="button" className="tp-btn sm" onClick={clearPad}><i className="bi bi-eraser" aria-hidden="true" />Clear</button>
          </div>
        </>
      )}

      {mode === 'upload' && (
        <>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg" hidden aria-label="Signature image"
            onChange={e => { loadFile(e.target.files?.[0]); e.target.value = ''; }} />
          {!preview ? (
            <div className={`tp-drop${over ? ' over' : ''}`} role="button" tabIndex={0}
              aria-label="Upload a signature image. Drag and drop, or press Enter to browse."
              onClick={() => fileRef.current?.click()}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileRef.current?.click(); } }}
              onDragOver={e => { e.preventDefault(); setOver(true); }}
              onDragLeave={() => setOver(false)}
              onDrop={onDrop}>
              <i className="bi bi-cloud-arrow-up tp-drop-ic" aria-hidden="true" />
              <span className="tp-drop-t">{over ? 'Drop your signature here' : 'Drag and drop your signature here'}</span>
              <span className="tp-hint">or <span className="tp-link">browse files</span> · PNG or JPG, up to 5 MB</span>
            </div>
          ) : (
            <>
              <div className="tp-checker"><img src={preview} alt="Signature preview" /></div>
              <div className="tp-row" style={{ marginTop: 10 }}>
                <label className="tp-check">
                  <input type="checkbox" checked={removeBg} onChange={e => setRemoveBg(e.target.checked)} />
                  Remove background
                </label>
                <span className="d-flex gap-2">
                  <button type="button" className="tp-btn sm" onClick={() => fileRef.current?.click()}><i className="bi bi-arrow-repeat" aria-hidden="true" />Replace</button>
                  <button type="button" className="tp-btn sm text-bad" onClick={resetUpload}><i className="bi bi-trash" aria-hidden="true" />Remove</button>
                </span>
              </div>
            </>
          )}
          {error && <div className="tp-error" role="alert"><i className="bi bi-exclamation-circle" aria-hidden="true" />{error}</div>}
          {!preview && <div className="tp-hint" style={{ marginTop: 8 }}>Use a clear photo of your signature on plain white paper. The background is removed for you.</div>}
        </>
      )}
    </div>
  );
}
